import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useQuery, useMutation } from 'react-fetching-library';
import { Button, CircularProgress, Alert, Skeleton, Stack, Box } from '@mui/material';
import { JsonForms } from '@jsonforms/react';
import { createAjv } from '@jsonforms/core';
import {
  materialCells,
  materialRenderers
} from '@jsonforms/material-renderers';
import ImageField, { ImageFieldTester } from './fields/ImageField';
import FileField, { FileFieldTester } from './fields/FileField';
import SkillsRangeField, {
  SkillsRangeFieldTester
} from './fields/SkillsRangeField';
import CheckBoxLabelHtml, {
  checkBoxLabelHtmlTester
} from './fields/CheckBoxLabelHtml';
import TextRenderer, { TextTester } from './layouts/Text';
import HTMLRenderer, { HTMLRendererTester } from './layouts/HTMLRenderer';
import DividerRenderer, { DividerTester } from './layouts/Divider';
import CopyButtonRenderer, { CopyButtonTester } from './layouts/CopyButton';
import HorizontalResponsiveRenderer, {
  HorizontalResponsiveTester
} from './layouts/HorizontalResponsive';
import CustomVerticalLayoutRenderer, {
  CustomVerticalLayoutTester
} from './layouts/CustomVerticalLayout';
import FieldsetDirectionColumnRenderer, {
  FieldsetDirectionColumnTester
} from './layouts/FieldsetDirectionColumn';
import CustomDateField, {
  CustomDateFieldTester
} from './fields/CustomDateField';
import RadioGroupFieldControl, {
  RadioGroupFieldControlTester
} from './fields/RadioGroupField';
import { useResponsive } from '../../services/hooks';
import {
  getLatestJsonFormAction,
  submitJsonFormAction
} from '../../services/request';
import ReCAPTCHA from 'react-google-recaptcha';
import FormSent from './FormSent';
import useAutoSaveDraft from './hooks/use-auto-save-draft';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import QualificationCardSent from './QualificationCardSent';

dayjs.locale('pl');

export default function Form({
  identifier,
  initData = {},
  admin = false,
  version = null,
  password = null,
  isDraft = true
}) {
  const ajv = createAjv();
  const recaptchaRef = React.createRef();
  const {
    mutate,
    loading: mutateLoading,
    error: mutateError
  } = useMutation(submitJsonFormAction);
  const [schema, setSchema] = useState({});
  const [schemaUI, setSchemaUI] = useState({});
  const [data, setData] = useState(initData);
  const [formId, setFormId] = useState(null);
  const [recaptcha, setRecaptcha] = useState(null);
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [isSent, setIsSent] = useState(!isDraft);
  const { loading, payload, error } = useQuery(
    getLatestJsonFormAction(identifier, admin, version)
  );
  const [errors, setErrors] = useState([]);
  const [validationMode, setValidationMode] = useState('ValidateAndHide');
  const isDesktop = useResponsive('up', 'lg');
  const { id } = useParams();
  const handleAutoSaveDraft = useAutoSaveDraft(id, identifier, isSent);

  const translator = (key, defaultMessage, args) => {
    if (key.endsWith('.required') || key.endsWith('.minItems')) {
      return 'To pole jest wymagane';
    }

    if (key.endsWith('.maxLength')) {
      return `Maksymalna liczba znaków to ${args.error.schema}`;
    }

    if (key.endsWith('.minLength')) {
      return `Minimalna liczba znaków to ${args.error.schema}`;
    }

    if (key.endsWith('.pattern') || key.endsWith('.format')) {
      return 'Niepoprawna wartość pola';
    }

    return defaultMessage;
  };

  const handleRecaptchaChange = value => {
    setRecaptcha(value);
  };

  const renderers = [
    ...materialRenderers,
    { tester: ImageFieldTester, renderer: ImageField },
    { tester: FileFieldTester, renderer: FileField },
    { tester: checkBoxLabelHtmlTester, renderer: CheckBoxLabelHtml },
    { tester: CustomDateFieldTester, renderer: CustomDateField },
    { tester: SkillsRangeFieldTester, renderer: SkillsRangeField },
    { tester: TextTester, renderer: TextRenderer },
    { tester: HTMLRendererTester, renderer: HTMLRenderer },
    { tester: DividerTester, renderer: DividerRenderer },
    { tester: CopyButtonTester, renderer: CopyButtonRenderer },
    {
      tester: HorizontalResponsiveTester,
      renderer: HorizontalResponsiveRenderer
    },
    {
      tester: CustomVerticalLayoutTester,
      renderer: CustomVerticalLayoutRenderer
    },
    {
      tester: FieldsetDirectionColumnTester,
      renderer: FieldsetDirectionColumnRenderer
    },
    { tester: RadioGroupFieldControlTester, renderer: RadioGroupFieldControl }
  ];

  ajv.addKeyword('expectedValue', {
    type: 'boolean',
    validate: function validate(schema, data) {
      if (data === schema) {
        return true;
      }
      validate.errors = [
        {
          keyword: 'expectedValue',
          message: 'To pole jest wymagane',
          params: { keyword: 'expectedValue' }
        }
      ];

      return false;
    },
    errors: true
  });

  const handleChange = (errors, newData) => {
    setErrors(errors); // błędy sprawdzamy zawsze
    // jeśli dane są identyczne — nie rób nic więcej
    if (_.isEqual(newData, data)) {
      return;
    }

    const cleanedData = { ...newData };

    Object.keys(newData).forEach(key => {
      const prevValue = data[key];
      const newValue = newData[key];

      if (prevValue !== newValue) {

        // Jeśli chcesz — reaguj tylko, gdy zmieniono np. na "Nie"
        // if (newValue === 'Nie' || newValue === false || newValue === '')

        const prefix = `${key}_`;
        Object.keys(cleanedData).forEach(otherKey => {
          if (otherKey.startsWith(prefix) && otherKey !== key) {
            Reflect.deleteProperty(cleanedData, otherKey);
          }
        });
      }
    });

    if (!_.isEqual(cleanedData, data)) {
      setData(cleanedData);
      handleAutoSaveDraft(cleanedData);
    }
  };

  const handleSubmit = async () => {
    if (_.isNil(recaptcha) && showRecaptcha) {
      return;
    }

    setValidationMode('ValidateAndShow');
    let visibleErrors = 0;

    errors.forEach(error => {
      // błędy "required" wskazują na brakującą właściwość obiektu-rodzica
      // (error.instancePath), więc ścieżkę pola budujemy z missingProperty —
      // dla każdego innego rodzaju błędu (type, format, minLength, ...)
      // instancePath już wskazuje bezpośrednio na wadliwe pole
      const path = error.keyword === 'required'
        ? `${error.instancePath}/${error.params.missingProperty}`
        : error.instancePath;

      if (document.querySelector(`[id^="#/properties${path}"]`) !== null) {
        visibleErrors++;
      }
    });

    if (visibleErrors === 0) {
      const dataCopy = _.cloneDeep(data);
      const images = [];
      const files = [];

      Object.keys(dataCopy).forEach(key => {
        if (key.endsWith('_image')) {
          images.push({ key, img: dataCopy[key] });
          Reflect.deleteProperty(dataCopy, key);
        }
        if (key.endsWith('_file')) {
          files.push({ key, img: dataCopy[key] });
          Reflect.deleteProperty(dataCopy, key);
        }
      });

      const formData = new FormData();
      images.forEach(img => formData.append(img.key, img.img));
      files.forEach(img => formData.append(img.key, img.img));
      formData.append('data', JSON.stringify(dataCopy));
      if (showRecaptcha) {
        formData.append('recaptcha', recaptcha);
      }

      const { error, payload } = await mutate(formData, formId);
      if (!error) {
        setIsSent(true);
      }
    }
  };

  useEffect(() => {
    if (!error && !loading) {
      setSchema(payload.json_schema);
      setSchemaUI(payload.json_schema_ui);
      setFormId(payload.id);
      setShowRecaptcha(payload.recaptcha);
    }
  }, [loading]);

  if (loading || _.isEmpty(schema) || _.isEmpty(schemaUI)) {
    return (
      <Skeleton
        variant="rectangular"
        style={{
          width: isDesktop ? '70vw' : '96vw',
          margin: '0 auto',
          height: '100%',
          borderRadius: '8px/13.3px'
        }}
      />
    );
  }

  if (error) {
    return <Alert severity="error">Wystąpił błąd</Alert>;
  }

  if (isSent) {
    if (identifier.startsWith('karta_kwalifikacyjna')) {
      return <QualificationCardSent identifier={identifier} data={data} password={password} />;
    }

    return <FormSent identifier={identifier} data={data} />;
  }

  return (
    <div style={{ width: isDesktop ? '70vw' : '96vw', margin: '0 auto' }}>
      <JsonForms
        ajv={ajv}
        i18n={{ locale: 'pl', translate: translator }}
        schema={schema}
        uischema={schemaUI}
        data={data}
        renderers={renderers}
        cells={materialCells}
        onChange={({ errors, data: newData }) => {
          handleChange(errors, newData);
        }}
        validationMode={validationMode}
      />
      { (validationMode === 'ValidateAndShow' && !mutateLoading)
        ? <Alert severity="error" variant="filled">
          Prosimy o wypełnienie wszystkich pól
        </Alert>
        : <Box height={50}></Box>}
      {showRecaptcha ? (
        <div>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_SITE_KEY}
            onChange={handleRecaptchaChange}
          />
        </div>
      ) : null}

      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        {mutateError ? (
          <Alert style={{ marginBottom: '20px' }} severity="error">
            Nie udało się przesłać formularza
          </Alert>
        ) : null}
        {mutateLoading ? (
          <CircularProgress size={36} />
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={_.isNil(recaptcha) && showRecaptcha}
          >
            Zatwierdź i wyślij
          </Button>
        )}
        <Box height={30}></Box>
      </div>
    </div>
  );
}
